import { apiClient } from '@/api/client';

/**
 * T75 — wires the "Export to Excel" buttons to the 6 T74 export endpoints
 * (`/students/export`, `/student-affairs/export`, `/accounts/export`, `/leads/export`,
 * `/reports/sales/export`, `/exam-bookings/export`). Every export endpoint takes the *same*
 * filter/search/sort query params as its list counterpart, but — unlike the list endpoint —
 * rejects `page`/`pageSize` outright (`VALIDATION_ERROR` / "property page should not exist"),
 * since an export always streams the *entire* filtered result set, never one page of it. Callers
 * must build their params object accordingly (i.e. reuse the list filters, but never page/pageSize).
 */

/** RFC 5987 `filename*=UTF-8''...` form (preferred — handles non-ASCII) or the plain
 * `filename="..."` form. Falls back to a sensible caller-supplied default when the
 * `Content-Disposition` header is absent or unparseable so a download is never nameless. */
const FILENAME_STAR_RE = /filename\*=(?:UTF-8'')?([^;]+)/i;
const FILENAME_RE = /filename="?([^";]+)"?/i;

export function extractFilename(contentDisposition: unknown, fallback: string): string {
  if (typeof contentDisposition !== 'string' || !contentDisposition) return fallback;

  const starMatch = FILENAME_STAR_RE.exec(contentDisposition);
  if (starMatch) {
    try {
      return decodeURIComponent(starMatch[1].trim());
    } catch {
      return starMatch[1].trim();
    }
  }

  const match = FILENAME_RE.exec(contentDisposition);
  return match ? match[1].trim() : fallback;
}

/** Object-URL + temporary `<a download>` click, the standard way to save an in-memory `Blob` to
 * disk from the browser without navigating away from the SPA. The URL is revoked on a short delay
 * rather than synchronously — some browsers (Firefox/Safari) need a tick to start reading from it
 * once `click()` returns. */
function triggerBrowserDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * GETs `endpoint` with `params` (the caller's *current* list-page filters — minus `page`/
 * `pageSize`, which export endpoints reject) and the same Bearer auth as every other request
 * (via `apiClient`, so the 401-refresh flow still applies), reads the response as a `Blob`, and
 * triggers a browser download using the server-supplied filename (falling back to
 * `fallbackFilename` if the header is missing). Rejects with the normal `ApiError` (via the
 * shared response interceptor) on failure — including a 403 for a role that can view the list but
 * isn't allowed to export it — for the caller to handle.
 */
export async function downloadExcelExport(
  endpoint: string,
  params: Record<string, unknown> | undefined,
  fallbackFilename: string,
): Promise<void> {
  const response = await apiClient.get<Blob>(endpoint, {
    params,
    responseType: 'blob',
  });

  const filename = extractFilename(response.headers['content-disposition'], fallbackFilename);
  triggerBrowserDownload(response.data, filename);
}
